import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGoUp, useWorkspaceNavigate } from '../../workspace-path';
import {
  currencySettingsSchema,
  leaveQuotaSchema,
  skillLevelSchema,
  skillSchema,
  type MemberSkill,
  type Skill,
  type SkillLevel,
  leaveTypeSchema,
  metafieldSchema,
  personSchema,
  rateAssignmentListSchema,
  rateCardTemplateSchema,
  projectRoleSchema,
  roleSchema,
  workScheduleAssignmentSchema,
  workScheduleTemplateSchema,
  type LeaveQuota,
  type LeaveType,
  type Metafield,
  type Person,
  type RateAssignment,
  type RateCardTemplate,
  type WorkScheduleAssignment,
  type WorkScheduleTemplate,
  type WorkspaceCurrency,
  identityRowSchema,
  formatSort,
  type SortKey,
} from '@forecastify/shared';
import { nextSurface, Toaster, type Toast } from '@forecastify/ui';
import { usePages } from '../use-pages';
import { useFirstLoad } from '../use-first-load';
import { useT, type Translate } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { PEOPLE_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { bulkTargets, postBulk } from '../use-bulk';
import { parseList, totalOf, usePermissionSet } from '../permissions-hook';
import { PeopleScreen } from './people-screen';
import { InvitationsPanel } from '../invitations/invitations-panel';
import { PersonScreen } from './person-screen';
import { usePersonDetail } from './use-person-detail';
import { useTeams } from '../use-teams';
import { PersonSurface, type PersonMode } from './person-surface';
import {
  OnboardingWizard,
  onboardingQuotas,
  onboardingRate,
  onboardingSchedule,
  type OnboardingDraft,
} from './onboarding-wizard';
import { requestAll } from '../read-all';
import {
  rememberedSort,
  useListMemory,
  useRememberEffect,
  useRememberedBoolean,
  useRememberedString,
} from '../list-memory';

/**
 * C14's two routes. `/people` lists, `/people/:id` is one person.
 *
 * ⚠️ **The filters live here and become query parameters**, because the service filters in SQL. A
 * filter applied in the browser would be a second implementation of the same predicate, and C12's
 * cursor work is the standing argument for not having one.
 */

let toastCounter = 0;
const nextToastId = (): string => `people-${String((toastCounter += 1))}`;

/** Just the three fields the wizard's role checkboxes need, out of C5's much larger role. */
interface RoleOption {
  id: string;
  name: string;
  code: string;
}

/** 17 Oct 2026 · a project role, for *Alap szerep* — what a person works as by default. */
export interface ProjectRoleOption {
  readonly id: string;
  readonly name: string;
  readonly color: string;
}

/**
 * The wizard's reference data, fetched once per route.
 *
 * ⚠️ **`/permissions/roles` and `person` metafields, both NON-FATAL.** A caller who can onboard but
 * cannot read the role catalogue should still get the list screen; an empty roles array renders a
 * wizard whose permission step offers nothing, which is honest. Making either fatal would take down
 * a screen over a tab nobody opened — the rule `task-route.tsx` already records.
 */
function useOnboardingData(
  session: ReturnType<typeof useSession>,
  canRate: boolean,
): {
  roles: readonly RoleOption[];
  projectRoles: readonly ProjectRoleOption[];
  metafields: readonly Metafield[];
  rateTemplates: readonly RateCardTemplate[];
  currencies: readonly WorkspaceCurrency[];
  rates: readonly RateAssignment[];
  scheduleTemplates: readonly WorkScheduleTemplate[];
  schedules: readonly WorkScheduleAssignment[];
  leaveTypes: readonly LeaveType[];
  quotas: readonly LeaveQuota[];
  skillVocabulary: readonly Skill[];
  skillLevels: readonly SkillLevel[];
  memberSkills: readonly MemberSkill[];
} {
  const [roles, setRoles] = useState<readonly RoleOption[]>([]);
  const [projectRoles, setProjectRoles] = useState<readonly ProjectRoleOption[]>([]);
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  /**
   * C61 · ⚠️ **Only when the caller may actually price somebody**, because two of these three reads
   * are the workspace's payroll — `rate-card.service.ts` guards them with `settings.view` and a
   * 403 in the console on every people page would be noise that trains people to ignore it.
   */
  const [rateTemplates, setRateTemplates] = useState<readonly RateCardTemplate[]>([]);
  const [currencies, setCurrencies] = useState<readonly WorkspaceCurrency[]>([]);
  const [rates, setRates] = useState<readonly RateAssignment[]>([]);
  /* C64 · the `sched` and `leave` steps' reference data, on the same permission. */
  const [scheduleTemplates, setScheduleTemplates] = useState<readonly WorkScheduleTemplate[]>([]);
  const [schedules, setSchedules] = useState<readonly WorkScheduleAssignment[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<readonly LeaveType[]>([]);
  const [quotas, setQuotas] = useState<readonly LeaveQuota[]>([]);
  /**
   * C54 · ⚠️⚠️ **NOT behind `canRate`, unlike the six above it.** The vocabulary needs no
   * permission at all — `skill.service.ts` says why — so gating these on `settings.view` would
   * blank the step for exactly the team lead who is most likely to be running the wizard.
   *
   * ⚠️ `memberSkills` is loaded per person when the wizard opens rather than for the whole
   * workspace: a list screen has no use for it, and one row per person per skill over a
   * two-hundred-person workspace is a read nobody asked for.
   */
  const [skillVocabulary, setSkillVocabulary] = useState<readonly Skill[]>([]);
  const [skillLevels, setSkillLevels] = useState<readonly SkillLevel[]>([]);
  const [memberSkills] = useState<readonly MemberSkill[]>([]);

  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;

      const year = new Date().getFullYear();
      const [
        roleList,
        fieldList,
        templateList,
        currencyList,
        assignmentList,
        scheduleTemplateList,
        scheduleList,
        typeList,
        quotaList,
        skillList,
        skillLevelList,
        projectRoleList,
      ] = await Promise.all([
        session.request('/permissions/roles').catch(() => null),
        session.request('/metafields?entity=person').catch(() => null),
        canRate ? session.request('/workspace/rate-cards').catch(() => null) : null,
        canRate ? session.request('/workspace/currencies').catch(() => null) : null,
        canRate ? session.request('/workspace/rate-cards/assignments/all').catch(() => null) : null,
        canRate ? session.request('/workspace/schedules/templates').catch(() => null) : null,
        canRate ? session.request('/workspace/schedules/assignments').catch(() => null) : null,
        canRate ? session.request('/workspace/leave/types').catch(() => null) : null,
        canRate
          ? session.request(`/workspace/leave/quotas?year=${String(year)}`).catch(() => null)
          : null,
        session.request('/skills').catch(() => null),
        session.request('/skills/levels').catch(() => null),
        /* 17 Oct 2026 · *Alap szerep*'s options — a vocabulary every member may read. */
        session.request('/project-roles').catch(() => null),
      ]);

      if (projectRoleList?.ok === true) {
        const parsed = parseList(projectRoleSchema, await projectRoleList.json());
        if (parsed !== null) {
          setProjectRoles(
            parsed.map((role) => ({ id: role.id, name: role.name, color: role.color })),
          );
        }
      }

      if (skillList?.ok === true) {
        const parsed = parseList(skillSchema, await skillList.json());
        if (parsed !== null) setSkillVocabulary(parsed);
      }
      if (skillLevelList?.ok === true) {
        const parsed = parseList(skillLevelSchema, await skillLevelList.json());
        if (parsed !== null) setSkillLevels(parsed);
      }

      if (scheduleTemplateList?.ok === true) {
        const parsed = parseList(workScheduleTemplateSchema, await scheduleTemplateList.json());
        if (parsed !== null) setScheduleTemplates(parsed.filter((one) => one.active));
      }
      if (scheduleList?.ok === true) {
        const parsed = parseList(workScheduleAssignmentSchema, await scheduleList.json());
        if (parsed !== null) setSchedules(parsed);
      }
      if (typeList?.ok === true) {
        const parsed = parseList(leaveTypeSchema, await typeList.json());
        /* ⚠️ Only the quota-bearing ones: an unlimited type has no entitlement to set. */
        if (parsed !== null) {
          setLeaveTypes(parsed.filter((one) => one.active && one.quotaSource === 'quota'));
        }
      }
      if (quotaList?.ok === true) {
        const parsed = parseList(leaveQuotaSchema, await quotaList.json());
        if (parsed !== null) setQuotas(parsed);
      }

      if (templateList?.ok === true) {
        const parsed = parseList(rateCardTemplateSchema, await templateList.json());
        /* ⚠️ Active only: an archived card is a record, not an offer. */
        if (parsed !== null) setRateTemplates(parsed.filter((one) => one.active));
      }
      if (currencyList?.ok === true) {
        const parsed = currencySettingsSchema.safeParse(await currencyList.json());
        if (parsed.success) setCurrencies(parsed.data.currencies.filter((one) => one.active));
      }
      if (assignmentList?.ok === true) {
        const parsed = rateAssignmentListSchema.safeParse(await assignmentList.json());
        if (parsed.success) setRates(parsed.data.assignments);
      }

      if (roleList?.ok === true) {
        const parsed = parseList(roleSchema, await roleList.json());
        if (parsed !== null) {
          setRoles(parsed.map((role) => ({ id: role.id, name: role.name, code: role.code })));
        }
      }
      if (fieldList?.ok === true) {
        const parsed = parseList(metafieldSchema, await fieldList.json());
        if (parsed !== null) setMetafields(parsed);
      }
    })();
  }, [session, canRate]);

  return {
    roles,
    projectRoles,
    metafields,
    rateTemplates,
    currencies,
    rates,
    scheduleTemplates,
    schedules,
    leaveTypes,
    quotas,
    skillVocabulary,
    skillLevels,
    memberSkills,
  };
}

/**
 * The wizard's draft, as the endpoint's body.
 *
 * ⚠️ **An empty string is a NULL on the wire, not an empty string.** The pickers use `''` for "not
 * chosen" because a `<select>` value has to be a string; sending that through would store an empty
 * team, and `team ?? '–'` would then render nothing instead of an em-dash forever.
 */
export function onboardingBody(draft: OnboardingDraft, version: number): string {
  const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

  return JSON.stringify({
    phone: orNull(draft.phone),
    employmentType: draft.employmentType,
    teamIds: draft.teamIds,
    managerMemberId: orNull(draft.managerMemberId),
    defaultProjectRoleId: orNull(draft.defaultProjectRoleId),
    location: orNull(draft.location),
    timezone: draft.timezone,
    language: draft.language,
    tracksTime: draft.tracksTime,
    resourcePlanned: draft.resourcePlanned,
    startsOn: orNull(draft.startsOn),
    roleIds: draft.roleIds,
    metafields: draft.metafields,
    /* C61 · ⚠️ `null` is the skip, and `onboardingRate` is the one place the strings become
       figures — see its note on why an unpicked template is a skip rather than a refusal. */
    rate: onboardingRate(draft),
    /* C64 · the schedule is `null` for somebody who does not track time — see `onboardingSchedule`. */
    schedule: onboardingSchedule(draft),
    leaveManaged: draft.leaveManaged,
    leaveQuotas: onboardingQuotas(draft),
    /* C54 · ⚠️ The WHOLE set, not a delta — the step seeds from what the person already holds,
       so anything missing from it was removed on purpose. The server clears and re-inserts. */
    skills: draft.skills.map((row) => ({ skillId: row.skillId, levelId: row.levelId })),
    version,
  });
}

/** The write-path refusals, worded once for both routes. */
function messageFor(t: Translate, code: string, detail: string): string {
  if (code === 'member_still_active') return t('people.error.stillActive', { name: detail });
  if (code === 'unknown_skill') return t('people.error.unknownSkill');
  if (code === 'unknown_project_role') return t('people.error.unknownProjectRole');
  if (code === 'member_deleted') return t('people.error.deleted');
  /* U-20 · on this screen the only write that can say it is planning a non-logger. */
  if (code === 'member_not_tracking') return t('people.error.notPlannable', { name: detail });
  /* R2 · 3 Nov 2026 · a deactivated line manager. */
  if (code === 'member_not_active') return t('error.member_not_active', { name: detail });
  /* ⚠️ The two manager refusals stay distinct — `details[0].code` carries which. */
  if (code === 'invalid_manager') {
    return detail === 'cycle' ? t('people.error.managerCycle') : t('people.error.managerSelf');
  }
  /* ⚠️ The escalation guard's own sentence. `error.unavailable` would tell somebody to retry a
   * request that will be refused identically forever. */
  /* ⚠️ Reactivation is a seat, and this is the only place in the product where that is visible on a
   * screen about a person rather than about an invitation. `error.unavailable` would tell somebody to
   * retry an act that will be refused identically until a seat frees. */
  if (code === 'seat_limit_reached') return t('people.error.seatLimit');
  if (code === 'unknown_rate_template') return t('onboarding.error.unknownTemplate');
  if (code === 'unknown_schedule_template') return t('onboarding.error.unknownSchedule');
  if (code === 'unknown_leave_type') return t('onboarding.error.unknownLeaveType');
  if (code === 'unknown_currency') return t('onboarding.error.unknownCurrency');
  if (code === 'cannot_change_own_roles') return t('onboarding.error.ownRoles');
  if (code === 'unknown_role') return t('onboarding.error.unknownRole');
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('error.not_found');
  if (code === 'validation_failed') return t('error.unavailable');
  return t('error.unavailable');
}

export function PeopleRoute() {
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  /* ⚠️ The schedule card's *Módosítás* — the settings screen that owns the templates. */
  const goToSchedules = (): void => {
    go('/settings/schedules');
  };
  /* ⚠️ The DÍJ card's *Módosítás* — straight to the Hozzárendelések tab, which is where a
     person's rate is actually changed. The mockup's `persRateEdit` opens exactly that. */
  const goToRates = (): void => {
    go('/settings/rates');
  };
  const t = useT();
  const permissions = usePermissionSet();

  /**
   * ⚠️ **Item 46 · the person a row click opens, over this list.** `null` is nobody selected, which
   * is an ordinary state rather than a reason not to call the hook.
   */
  const [openPersonId, setOpenPersonId] = useState<string | null>(null);
  const [personMode, setPersonMode] = useState<PersonMode>(nextSurface('page'));
  const opened = usePersonDetail(openPersonId);
  /** C54.1 · a refused skill write is a line beside the panel, not the list's error. */
  const [skillError, setSkillError] = useState<string | null>(null);
  const skillWrite = async (path: string, init: RequestInit): Promise<void> => {
    setSkillError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      setSkillError(t('people.skills.failed'));
      return;
    }
    await opened.reload();
  };
  /* ⚠️ Who is reading, from the session's workspace row — `tasks-route.tsx` argues the source. */
  const myMemberId =
    session.workspaces.find((workspace) => workspace.id === session.workspaceId)?.memberId ?? '';
  /* ⚠️ Everybody but this person, for the manager picker — the same rule the route applies. */
  const openedManagers = useMemo(
    () =>
      opened.colleagues
        /* R2 · 3 Nov 2026 · nor anyone not active (deactivated or invited) — except the manager they already have. */
        .filter(
          (candidate) =>
            candidate.id !== openPersonId &&
            candidate.deletedAt === null &&
            (candidate.status === 'active' || candidate.id === opened.person?.managerMemberId),
        )
        .map((candidate) => ({ id: candidate.id, name: candidate.displayName })),
    [opened.colleagues, openPersonId, opened.person?.managerMemberId],
  );

  const [people, setPeople] = useState<readonly Person[]>([]);
  /* R-60 · the list Settings → Teams keeps, live. */
  const teams = useTeams();
  /* 3 Nov 2026 · remembered per workspace and member (`list-memory.ts`). */
  const memory = useListMemory('people');
  const [search, setSearch] = useRememberedString(memory, 'search');
  const [team, setTeam] = useRememberedString(memory, 'teamId');
  const [status, setStatus] = useRememberedString(memory, 'status');
  const [employmentType, setEmploymentType] = useRememberedString(memory, 'employmentType');
  const [tracks, setTracks] = useRememberedString(memory, 'tracks');
  const [plan, setPlan] = useRememberedString(memory, 'plan');
  const [includeDeleted, setIncludeDeleted] = useRememberedBoolean(memory, 'includeDeleted');
  /**
   * ⚠️ **The sort resets the page too, through the same `filtered()` wrapper**, and the reason is
   * different from a filter's: re-sorting does not change WHICH people match, so page four still
   * exists — it just holds different people now, and staying on it lands somewhere nobody asked
   * to be.
   */
  const [sort, setSort] = useState<readonly SortKey[]>(() => rememberedSort(memory.restored));
  useRememberEffect(memory, { sort });
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /* Bumped by the header's invite button; the panel opens on the change. */
  const [inviteSignal, setInviteSignal] = useState(0);
  const [onboarding, setOnboarding] = useState<Person | null>(null);
  const {
    roles,
    projectRoles,
    metafields,
    rateTemplates,
    currencies,
    rates,
    scheduleTemplates,
    schedules,
    leaveTypes,
    quotas,
    skillVocabulary,
    skillLevels,
    memberSkills,
  } = useOnboardingData(session, permissions.has('settings.manage'));
  const pages = usePages();
  const { offset, settle: settlePage } = pages;
  /** ⚠️ See the filter handlers below: one place resets the page, not six. */
  const filtered =
    <T,>(set: (value: T) => void) =>
    (value: T): void => {
      pages.reset();
      set(value);
    };

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim() !== '') params.set('search', search.trim());
    if (team !== '') params.set('teamId', team);
    if (status !== '') params.set('status', status);
    if (employmentType !== '') params.set('employmentType', employmentType);
    /* ⚠️ `''` means "either", so the parameter is omitted rather than sent as `false`. */
    if (tracks !== '') params.set('tracksTime', tracks);
    if (plan !== '') params.set('resourcePlanned', plan);
    if (includeDeleted) params.set('includeDeleted', 'true');
    /* ⚠️ `name:asc,team:desc` — the ORDER is the priority, and the server maps each key through
       `PERSON_SORT`. Item 45, D3: the sort is the server's, never a reorder of the page we hold. */
    const encoded = formatSort(sort);
    if (encoded !== undefined) params.set('sort', encoded);
    /* ⚠️ An OFFSET since item 20 — `use-pages.ts` records why the cursor stack went. */
    if (offset > 0) params.set('offset', String(offset));
    const text = params.toString();
    return text === '' ? '' : `?${text}`;
    /* ⚠️ `offset` is an input to the query, so it belongs here — omitted, the string is built once
       and *Next* fetches the page it is already showing. */
  }, [search, team, status, employmentType, tracks, plan, includeDeleted, sort, offset]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const list = await session.request(`/people${query}`).catch(() => null);

    if (list === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!list.ok) {
      setFailure(await failureOf(list));
      return;
    }

    const body: unknown = await list.json();
    const parsed = parseList(personSchema, body);
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setPeople(parsed);
    settlePage(totalOf(body));

    /* ⚠️ `settlePage`, not `pages` — the latter is memoised over its own state, so depending on it
       would make this callback change every time it settled: an infinite reload. */
  }, [session, query, settlePage]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. A colleague's change
   * refetches this in place — no spinner, no blank, no reload. R-19. */
  useLiveInvalidation(PEOPLE_TOPICS, reload);

  const toast = (entry: Omit<Toast, 'id'>): void => {
    setToasts((current) => [...current, { ...entry, id: nextToastId() }]);
  };

  /**
   * ⚠️ **Item 72 · one request, and the answer is per row** — `bulk.ts`. The version travels with
   * each id, so a person somebody else has edited since the page loaded is refused rather than
   * overwritten, and the toast counts what LANDED (`state-rules.md` §5).
   */
  const runBulk = async (
    path: string,
    ids: readonly string[],
    body: Record<string, unknown>,
    key: 'people.toast.bulkEdited' | 'people.toast.bulkDeleted',
  ): Promise<void> => {
    const result = await postBulk(session, path, { ...body, targets: bulkTargets(people, ids) });
    await reload();
    if (result === null) {
      toast({ title: t('error.unavailable'), variant: 'error' });
      return;
    }
    toast({
      title: t(key, { count: result.done.length }),
      ...(result.failed.length === 0
        ? {}
        : { description: t('bulk.someRefused', { total: ids.length }) }),
      variant: result.failed.length === 0 ? 'success' : 'error',
    });
  };

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const envelope = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { code?: string; message?: string }[] };
      } | null;
      const code = envelope?.error?.code ?? 'unavailable';
      const first = envelope?.error?.details?.[0];
      setServerError(
        messageFor(t, code, first?.code === 'cycle' ? 'cycle' : (first?.message ?? '')),
      );
      return false;
    }
    await reload();
    return true;
  };

  /**
   * 26 Oct 2026 · ⚠️ **EVERYBODY, NOT THE PAGE ON SCREEN.** The wizard's manager picker was built
   * from the list's current page — fifty people, narrowed by whatever search or filter was set — so
   * on UAT it ended at *M*. Its own read, when the wizard opens, every page of it (`read-all.ts`,
   * the pickers' convention).
   */
  const [everyone, setEveryone] = useState<readonly Person[]>([]);
  const onboardingId = onboarding?.id ?? null;
  useEffect(() => {
    if (onboardingId === null) return;
    void (async () => {
      const response = await requestAll(session, '/people').catch(() => null);
      if (response?.ok !== true) return;
      setEveryone(parseList(personSchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [onboardingId]);

  /* ⚠️ Minus the person being onboarded, or the manager picker offers them themselves — which
   * `invalid_manager` would refuse after the whole wizard had been filled in. */
  const managers = useMemo(
    () =>
      (everyone.length > 0 ? everyone : people)
        /* R2 · 3 Nov 2026 · nor anyone not active (deactivated or invited) (`member_not_active`). */
        .filter(
          (candidate) =>
            candidate.id !== onboarding?.id &&
            candidate.deletedAt === null &&
            candidate.status === 'active',
        )
        .map((candidate) => ({ id: candidate.id, name: candidate.displayName })),
    [everyone, people, onboarding],
  );

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <PeopleScreen
          page={{
            hasPrevious: pages.hasPrevious,
            hasNext: pages.hasNext,
            onPrevious: pages.previous,
            onNext: pages.next,
            /* ⚠️ Item 20's `[<] [selector] [>] [total]`. The total counts what the filters
               left, which is what `list.pageTotal` says in words. */
            index: pages.index,
            count: pages.count,
            total: pages.total,
            onGoTo: pages.goTo,
            pageSize: pages.limit,
          }}
          loading={loading}
          people={people}
          metafields={metafields}
          teams={teams}
          search={search}
          team={team}
          status={status}
          employmentType={employmentType}
          tracks={tracks}
          plan={plan}
          includeDeleted={includeDeleted}
          canDeactivate={permissions.has('people.deactivate')}
          canOnboard={permissions.has('people.onboard')}
          serverError={serverError}
          /**
           * ⚠️⚠️ **EVERY FILTER RESETS THE PAGE, AND FORGETTING ONE SHOWS AN EMPTY LIST.** A cursor
           * is a position in the ordered set the previous filter produced; in the new one it almost
           * always lands past the end — which renders as *"nobody matches"* rather than as a bug.
           * `filtered()` wraps each setter so there is one place to be right rather than six.
           */
          onSearch={filtered(setSearch)}
          onTeam={filtered(setTeam)}
          onStatus={filtered(setStatus)}
          onEmploymentType={filtered(setEmploymentType)}
          onTracks={filtered(setTracks)}
          onPlan={filtered(setPlan)}
          onIncludeDeleted={filtered(setIncludeDeleted)}
          sort={sort}
          onSortChange={filtered(setSort)}
          {...(permissions.has('people.manage')
            ? {
                onBulkEdit: (ids: readonly string[], patch: Record<string, unknown>) => {
                  void runBulk('/people/bulk', ids, patch, 'people.toast.bulkEdited');
                },
              }
            : {})}
          {...(permissions.has('people.deactivate')
            ? {
                onBulkDeactivate: (ids: readonly string[]) => {
                  void runBulk('/people/bulk-delete', ids, {}, 'people.toast.bulkDeleted');
                },
              }
            : {})}
          /**
           * ⚠️⚠️ **A MODAL OVER THE LIST — items 34 and 46 — where this navigated away.** Every
           * look at a person cost the list: its page, its filters and its scroll position, all
           * re-fetched on the way back. `nextSurface('page')` is the ladder's answer for a
           * drill-in from a list, and the route stays for a deep link.
           */
          onOpen={(person) => {
            setOpenPersonId(person.id);
            setPersonMode(nextSurface('page'));
          }}
          /* ⚠️ `people.view` gates the LIST and `people.onboard` the button — two permissions,
             because seeing who is on their way in is part of seeing who is here. */
          /**
           * ⚠️⚠️ **THE PANEL RENDERS ONLY ITS DIALOG NOW — item 44.** The pending invitations are
           * rows in the table above, where they page, sort and filter with everybody; what stays
           * here is the invite dialog the header's *Meghívás* opens through `openSignal`, which has
           * nowhere else to live.
           */
          invitations={
            <InvitationsPanel
              canInvite={permissions.has('people.onboard')}
              openSignal={inviteSignal}
              showList={false}
            />
          }
          /**
           * ⚠️ **These act on an INVITATION id, not a member id** — the row menu branches on
           * `person.kind` before offering them.
           */
          {...(permissions.has('people.onboard')
            ? {
                onResendInvite: (invitationId: string) => {
                  void write(`/workspace/invitations/${invitationId}/resend`, { method: 'POST' });
                },
                onRevokeInvite: (invitationId: string) => {
                  void write(`/workspace/invitations/${invitationId}`, { method: 'DELETE' });
                },
              }
            : {})}
          /* ⚠️ The header's *Meghívás*, which the mockup puts there. Absent without the
             permission, so the button is never a promise the server refuses. */
          {...(permissions.has('people.onboard')
            ? {
                onInvite: () => {
                  setInviteSignal((n) => n + 1);
                },
              }
            : {})}
          onOnboard={(person) => {
            setServerError(null);
            setOnboarding(person);
          }}
          /* ux-pass-2 · ⚠️ `people.manage`, the permission the PATCH checks. */
          {...(permissions.has('people.manage')
            ? {
                onSetPlanned: (person: Person, planned: boolean) => {
                  void (async () => {
                    const ok = await write(`/people/${person.id}`, {
                      method: 'PATCH',
                      body: JSON.stringify({ resourcePlanned: planned, version: person.version }),
                    });
                    if (ok) {
                      toast({
                        title: planned ? t('people.toast.planOn') : t('people.toast.planOff'),
                        description: planned
                          ? t('people.toast.planOnNote', { name: person.displayName })
                          : t('people.toast.planOffNote', { name: person.displayName }),
                        variant: 'success',
                      });
                    }
                  })();
                },
              }
            : {})}
          onSetStatus={(person, next) => {
            void (async () => {
              const ok = await write(`/people/${person.id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status: next, version: person.version }),
              });
              if (ok) {
                toast({
                  title:
                    next === 'active' ? t('people.toast.activated') : t('people.toast.deactivated'),
                  description: person.displayName,
                  variant: 'success',
                });
              }
            })();
          }}
          onDelete={(person) => {
            void (async () => {
              /* ⚠️ The version rides in the QUERY: a body on `DELETE` is widely dropped by proxies,
               * so the concurrency check would silently stop being enforced. */
              const ok = await write(`/people/${person.id}?version=${String(person.version)}`, {
                method: 'DELETE',
              });
              if (ok) {
                toast({
                  title: t('people.toast.deleted'),
                  description: person.displayName,
                  variant: 'success',
                });
              }
            })();
          }}
        />
      </ScreenState>

      {/**
       * ⚠️⚠️ **ITEM 46 · THE PERSON, OVER THE LIST.** The list stays mounted underneath, which is
       * the point: a pass over twenty people is twenty peeks rather than twenty round trips
       * through a route that re-fetches the list each way.
       *
       * ⚠️ **Rendered only once its data has arrived.** `PersonScreen` takes a `Person`, not a
       * nullable one — an empty frame with a spinner in it would be a second loading state for a
       * screen that already has `ScreenState`.
       *
       * ⚠️ **`full` navigates**, because the full page IS the route. Maximising from here changes
       * the address rather than growing a modal, which is what makes the two consistent: the
       * address bar and the surface always agree about where you are.
       */}
      {opened.person === null ? null : (
        <PersonSurface
          mode={personMode}
          open
          person={opened.person}
          loading={opened.loading}
          projects={opened.theirProjects}
          taskCounts={opened.taskCounts}
          taskFacets={opened.taskFacets}
          logFacets={opened.logFacets}
          recentMonth={opened.recentMonth}
          nonce={opened.nonce}
          metafields={opened.metafields}
          managers={openedManagers}
          projectRoles={projectRoles}
          teams={opened.teams}
          canManage={permissions.has('people.manage')}
          canOnboard={permissions.has('people.onboard')}
          identities={opened.identities}
          schedule={opened.schedule}
          rate={opened.rate}
          /* ⚠️ `settings.view`, the permission the RATE READ checks — not `settings.manage`. A
             reader who may look at the settings may see what somebody costs; changing it is the
             link below, which needs the stronger one. */
          canSeeRates={permissions.has('settings.view')}
          {...(permissions.has('settings.manage') ? { onOpenRates: goToRates } : {})}
          /* ⚠️ Only for somebody who may manage settings — a link to a screen that refuses is
             worse than no link. The schedule itself is read-only here either way (C43). */
          {...(permissions.has('settings.manage') ? { onOpenSchedules: goToSchedules } : {})}
          /* ⚠️ `people.manage`, the permission the server checks on this endpoint. */
          /* C54.1 · the skills panel, as on the full page — it was drawn read-only here because
             none of this reached the drawer, so *+ Skill* never appeared (13 Oct 2026). */
          skills={opened.skills}
          skillVocabulary={skillVocabulary}
          skillLevels={skillLevels}
          skillError={skillError}
          {...(permissions.has('people.manage')
            ? {
                onSetSkill: (skillId: string, levelId: string) => {
                  void skillWrite(`/skills/members/${opened.person?.id ?? ''}`, {
                    method: 'PUT',
                    body: JSON.stringify({ skillId, levelId }),
                  });
                },
                onRemoveSkill: (skillId: string) => {
                  void skillWrite(`/skills/members/${opened.person?.id ?? ''}/${skillId}`, {
                    method: 'DELETE',
                  });
                },
              }
            : {})}
          roleOptions={roles}
          /* ⚠️ `people.manage`, and never on your own row — the server refuses both. */
          {...(permissions.has('people.manage') && opened.person.id !== myMemberId
            ? {
                onRole: (roleId: string) => {
                  void (async () => {
                    if (opened.person === null) return;
                    const ok = await write(`/people/${opened.person.id}/roles`, {
                      method: 'PUT',
                      body: JSON.stringify({ roleIds: [roleId], version: opened.person.version }),
                    });
                    if (ok) await opened.reload();
                  })();
                },
              }
            : {})}
          {...(permissions.has('people.manage')
            ? {
                onLeaveManaged: (managed: boolean) => {
                  void write(`/workspace/leave/managed/${opened.person?.id ?? ''}`, {
                    method: 'PUT',
                    body: JSON.stringify({ managed }),
                  });
                },
                /* ⚠️ `0116` · a document ABOUT this person, not their avatar. */
                onAttach: async (file: File) => {
                  const form = new FormData();
                  form.set('file', file);
                  await write(`/files?target=person&targetId=${opened.person?.id ?? ''}`, {
                    method: 'POST',
                    body: form,
                  });
                },
              }
            : {})}
          canPairIdentity={permissions.has('integration.manage')}
          onPairIdentity={(identityId, memberId) => {
            void (async () => {
              const response = await session
                .request(`/integrations/identities/${identityId}`, {
                  method: 'PUT',
                  body: JSON.stringify({ memberId }),
                })
                .catch(() => null);
              if (response?.ok !== true) return;
              opened.setIdentities(parseList(identityRowSchema, await response.json()) ?? []);
            })();
          }}
          serverError={serverError}
          onBack={() => {
            setOpenPersonId(null);
          }}
          onOpenTask={(task) => {
            go(`/tasks/${task.key}`);
          }}
          onOnboard={() => {
            setServerError(null);
            setOnboarding(opened.person);
          }}
          onStatus={(next) => {
            void (async () => {
              const target = opened.person;
              if (target === null) return;
              await write(`/people/${target.id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status: next, version: target.version }),
              });
              await opened.reload();
            })();
          }}
          onPatch={(patch) => {
            void (async () => {
              if (opened.person === null) return;
              await write(`/people/${opened.person.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ ...patch, version: opened.person.version }),
              });
              await opened.reload();
            })();
          }}
          onModeChange={(next) => {
            /* ⚠️ The full page IS the route — maximising changes the address rather than growing
               a modal, so the address bar and the surface never disagree. */
            if (next === 'full') {
              const id = opened.person?.id ?? '';
              setOpenPersonId(null);
              go(`/people/${id}`);
              return;
            }
            setPersonMode(next);
          }}
          onClose={() => {
            setOpenPersonId(null);
          }}
        />
      )}

      <OnboardingWizard
        person={onboarding}
        managers={managers}
        teams={teams}
        roles={roles}
        projectRoles={projectRoles}
        metafields={metafields}
        rateTemplates={rateTemplates}
        currencies={currencies}
        /* ⚠️ The rate in force TODAY for this person, out of the list the hook already holds —
           `listAssignments` carries one row per priced member, so there is nothing to fetch. */
        rateNow={rates.find((one) => one.memberId === onboarding?.id) ?? null}
        scheduleTemplates={scheduleTemplates}
        scheduleNow={schedules.find((one) => one.memberId === onboarding?.id) ?? null}
        leaveTypes={leaveTypes}
        skillVocabulary={skillVocabulary}
        skillLevels={skillLevels}
        /* ⚠️ C54 · empty for now — see `memberSkills` in the hook: the step seeds from what the
           person holds, and that read lands with the wizard rather than with the list. */
        skillsNow={memberSkills}
        quotasNow={quotas.filter((one) => one.memberId === onboarding?.id)}
        canSettings={permissions.has('settings.manage')}
        serverError={serverError}
        onClose={() => {
          setOnboarding(null);
        }}
        onFinish={(draft) => {
          void (async () => {
            if (onboarding === null) return;
            const ok = await write(`/people/${onboarding.id}/onboarding`, {
              method: 'POST',
              body: onboardingBody(draft, onboarding.version),
            });
            /* ⚠️ The dialog stays OPEN on a refusal, with the error inside it. Closing it would
             * throw away six steps of typing over one bad field. */
            if (ok) {
              setOnboarding(null);
              toast({
                title: t('onboarding.done'),
                description: t('onboarding.doneNote', { name: onboarding.displayName }),
                variant: 'success',
              });
            }
          })();
        }}
      />

      <Toaster
        toasts={[...toasts]}
        label={t('time.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}

export function PersonRoute() {
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const goUp = useGoUp();
  /* ⚠️ The schedule card's *Módosítás* — the settings screen that owns the templates. */
  const goToSchedules = (): void => {
    go('/settings/schedules');
  };
  /* ⚠️ The DÍJ card's *Módosítás* — straight to the Hozzárendelések tab, which is where a
     person's rate is actually changed. The mockup's `persRateEdit` opens exactly that. */
  const goToRates = (): void => {
    go('/settings/rates');
  };
  const params = useParams();
  const t = useT();
  const permissions = usePermissionSet();
  const id = params.id ?? '';

  /**
   * ⚠️⚠️ **THE LOADER MOVED TO `use-person-detail.ts` FOR ITEM 46.** The list opens a person as a
   * modal now, and both surfaces need the same nine requests — a second copy in the list route is
   * the shape that drifts, where the one nobody opens stops asking for a field the screen has
   * started drawing.
   */
  const detail = usePersonDetail(id);
  /* ⚠️ Who is reading, from the session's workspace row — `tasks-route.tsx` argues the source. */
  const myMemberId =
    session.workspaces.find((workspace) => workspace.id === session.workspaceId)?.memberId ?? '';
  const {
    person,
    metafields,
    colleagues,
    teams,
    identities,
    failure,
    loading,
    setIdentities,
    reload,
    theirProjects,
    schedule,
    rate,
    /**
     * C54.1 · ⚠️ **Only THIS PERSON's skills come from the detail hook.** The vocabulary and
     * the levels are workspace-wide and `useOnboardingData` below already holds both for the
     * wizard — fetching them a second time here would be two requests for one answer, and two
     * places for the level order to be read differently.
     */
    skills,
  } = detail;
  const [serverError, setServerError] = useState<string | null>(null);
  /**
   * C54.1 · ⚠️ **Its own error, not the screen's.** A refused skill write is a message beside
   * the panel; `serverError` above replaces the page, which is the wrong surface for *"this level
   * no longer exists"*.
   */
  const [skillError, setSkillError] = useState<string | null>(null);

  /** ⚠️ `PUT`, because one level per skill per person makes it idempotent by shape. */
  const writeSkill = async (skillId: string, levelId: string): Promise<void> => {
    setSkillError(null);
    const response = await session
      .request(`/skills/members/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ skillId, levelId }),
      })
      .catch(() => null);
    if (response?.ok !== true) {
      setSkillError(t('people.skills.failed'));
      return;
    }
    await reload();
  };

  const dropSkill = async (skillId: string): Promise<void> => {
    setSkillError(null);
    const response = await session
      .request(`/skills/members/${id}/${skillId}`, { method: 'DELETE' })
      .catch(() => null);
    if (response?.ok !== true) {
      setSkillError(t('people.skills.failed'));
      return;
    }
    await reload();
  };
  const [onboarding, setOnboarding] = useState(false);
  /* ⚠️ The onboarding wizard's roles — its own feed, not part of the person's detail. C61 adds the
     rate cards and currencies; the person's own rate comes from `detail`, which already read it. */
  const {
    roles,
    projectRoles,
    rateTemplates,
    currencies,
    scheduleTemplates,
    schedules,
    leaveTypes,
    quotas,
    skillVocabulary,
    skillLevels,
    memberSkills,
  } = useOnboardingData(session, permissions.has('settings.manage'));

  const managers = useMemo(
    () =>
      colleagues
        /* R2 · 3 Nov 2026 · nor anyone not active (deactivated or invited) — except the manager they already have. */
        .filter(
          (candidate) =>
            candidate.id !== id &&
            candidate.deletedAt === null &&
            (candidate.status === 'active' || candidate.id === person?.managerMemberId),
        )
        .map((candidate) => ({ id: candidate.id, name: candidate.displayName })),
    [colleagues, id, person?.managerMemberId],
  );

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      {person === null ? null : (
        <>
          <PersonScreen
            person={person}
            loading={loading}
            projects={theirProjects}
            taskCounts={detail.taskCounts}
            taskFacets={detail.taskFacets}
            logFacets={detail.logFacets}
            recentMonth={detail.recentMonth}
            nonce={detail.nonce}
            metafields={metafields}
            managers={managers}
            teams={teams}
            projectRoles={projectRoles}
            canManage={permissions.has('people.manage')}
            canOnboard={permissions.has('people.onboard')}
            identities={identities}
            schedule={schedule}
            rate={rate}
            /* C54.1 · the skills panel. The write handlers are absent without `people.manage`,
               which is what makes the panel read-only rather than disabled. */
            skills={skills}
            skillVocabulary={skillVocabulary}
            skillLevels={skillLevels}
            skillError={skillError}
            {...(permissions.has('people.manage')
              ? {
                  onSetSkill: (skillId: string, levelId: string) => {
                    void writeSkill(skillId, levelId);
                  },
                  onRemoveSkill: (skillId: string) => {
                    void dropSkill(skillId);
                  },
                }
              : {})}
            /* ⚠️ `settings.view` — see the modal above. */
            canSeeRates={permissions.has('settings.view')}
            {...(permissions.has('settings.manage') ? { onOpenRates: goToRates } : {})}
            {...(permissions.has('settings.manage') ? { onOpenSchedules: goToSchedules } : {})}
            roleOptions={roles}
            /* ⚠️ `people.manage`, and never on your own page — the server refuses both. */
            {...(permissions.has('people.manage') && person.id !== myMemberId
              ? {
                  onRole: (roleId: string) => {
                    void (async () => {
                      setServerError(null);
                      const response = await session
                        .request(`/people/${person.id}/roles`, {
                          method: 'PUT',
                          body: JSON.stringify({ roleIds: [roleId], version: person.version }),
                        })
                        .catch(() => null);
                      if (response?.ok !== true) {
                        setServerError(
                          response?.status === 409 ? t('error.conflict') : t('error.unavailable'),
                        );
                      }
                      await reload();
                    })();
                  },
                }
              : {})}
            /* ⚠️ `people.manage`, the permission the server checks on this endpoint. */
            {...(permissions.has('people.manage')
              ? {
                  onLeaveManaged: (managed: boolean) => {
                    void (async () => {
                      setServerError(null);
                      const response = await session
                        .request(`/workspace/leave/managed/${id}`, {
                          method: 'PUT',
                          body: JSON.stringify({ managed }),
                        })
                        .catch(() => null);
                      if (response?.ok !== true) {
                        setServerError(t('error.unavailable'));
                        return;
                      }
                      await reload();
                    })();
                  },
                  /* ⚠️ `0116` · a document ABOUT this person, not their avatar. */
                  onAttach: async (file: File) => {
                    const form = new FormData();
                    form.set('file', file);
                    await session
                      .request(`/files?target=person&targetId=${id}`, {
                        method: 'POST',
                        body: form,
                      })
                      .catch(() => null);
                    await reload();
                  },
                }
              : {})}
            /* ⚠️ `integration.manage`, not `people.manage`: deciding who somebody IS in the tracker
               is an integration decision that happens to be asked on a person's page. */
            canPairIdentity={permissions.has('integration.manage')}
            onPairIdentity={(identityId, memberId) => {
              void (async () => {
                const response = await session
                  .request(`/integrations/identities/${identityId}`, {
                    method: 'PUT',
                    body: JSON.stringify({ memberId }),
                  })
                  .catch(() => null);
                if (response?.ok !== true) {
                  /* ⚠️ The code, not a generic failure: `conflict` here means the account holder
                     proved this pairing themselves (`0085`), which is a different thing from a
                     server being unreachable and needs a different sentence. */
                  const envelope = (await response?.json().catch(() => null)) as {
                    error?: { code?: string };
                  } | null;
                  setServerError(messageFor(t, envelope?.error?.code ?? 'unavailable', ''));
                  return;
                }
                setIdentities(parseList(identityRowSchema, await response.json()) ?? []);
              })();
            }}
            serverError={serverError}
            /* ⚠️ Up to the list, wherever the person was opened from (R-61, 3 Nov 2026). */
            onBack={() => {
              goUp('/people');
            }}
            onOpenTask={(task) => {
              go(`/tasks/${task.key}`);
            }}
            onOnboard={() => {
              setServerError(null);
              setOnboarding(true);
            }}
            /**
             * ⚠️ **Its own endpoint, not a field patch.** The status carries a version and a
             * consequence — somebody stops being able to sign in — and `PATCH /people/:id` is
             * shaped for one field at a time, with no place to say either.
             *
             * ⚠️ This route has no `write` helper of its own; it reports through `serverError` and
             * reloads, exactly as `onPatch` beside it does.
             */
            onStatus={(next) => {
              void (async () => {
                setServerError(null);
                const response = await session
                  .request(`/people/${id}/status`, {
                    method: 'PUT',
                    body: JSON.stringify({ status: next, version: person.version }),
                  })
                  .catch(() => null);

                if (response === null) {
                  setServerError(t('error.unavailable'));
                  return;
                }
                if (!response.ok) {
                  const envelope = (await response.json().catch(() => null)) as {
                    error?: { code?: string };
                  } | null;
                  setServerError(messageFor(t, envelope?.error?.code ?? 'unavailable', ''));
                  return;
                }
                await reload();
              })();
            }}
            onPatch={(patch) => {
              void (async () => {
                setServerError(null);
                const response = await session
                  .request(`/people/${id}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ ...patch, version: person.version }),
                  })
                  .catch(() => null);

                if (response === null) {
                  setServerError(t('error.unavailable'));
                  return;
                }
                if (!response.ok) {
                  const envelope = (await response.json().catch(() => null)) as {
                    error?: { code?: string; details?: { code?: string; message?: string }[] };
                  } | null;
                  const code = envelope?.error?.code ?? 'unavailable';
                  const first = envelope?.error?.details?.[0];
                  setServerError(
                    messageFor(t, code, first?.code === 'cycle' ? 'cycle' : (first?.message ?? '')),
                  );
                  return;
                }
                await reload();
              })();
            }}
          />

          {/* ⚠️ The detail route reuses the person's OWN metafield definitions and manager list —
              already fetched for the form below, so the wizard adds one request, for the roles. */}
          <OnboardingWizard
            person={onboarding ? person : null}
            managers={managers}
            teams={teams}
            roles={roles}
            projectRoles={projectRoles}
            metafields={metafields}
            rateTemplates={rateTemplates}
            currencies={currencies}
            rateNow={rate}
            scheduleTemplates={scheduleTemplates}
            /**
             * ⚠️ **The ASSIGNMENT, not `detail.schedule`.** The detail's row is the resolved
             * shape in force today — seven hours and a type, with no id and no start date — which
             * is what the Munkarend card draws. The wizard has to write over the row it read, so
             * it needs the row: `fromDate` is half of that table's key.
             */
            scheduleNow={schedules.find((one) => one.memberId === id) ?? null}
            leaveTypes={leaveTypes}
            skillVocabulary={skillVocabulary}
            skillLevels={skillLevels}
            /* ⚠️ C54 · empty for now — see `memberSkills` in the hook: the step seeds from what the
           person holds, and that read lands with the wizard rather than with the list. */
            skillsNow={memberSkills}
            quotasNow={quotas.filter((one) => one.memberId === id)}
            canSettings={permissions.has('settings.manage')}
            serverError={serverError}
            onClose={() => {
              setOnboarding(false);
            }}
            onFinish={(draft) => {
              void (async () => {
                setServerError(null);
                const response = await session
                  .request(`/people/${id}/onboarding`, {
                    method: 'POST',
                    body: onboardingBody(draft, person.version),
                  })
                  .catch(() => null);

                if (response === null) {
                  setServerError(t('error.unavailable'));
                  return;
                }
                if (!response.ok) {
                  const envelope = (await response.json().catch(() => null)) as {
                    error?: { code?: string; details?: { code?: string; message?: string }[] };
                  } | null;
                  const code = envelope?.error?.code ?? 'unavailable';
                  const first = envelope?.error?.details?.[0];
                  setServerError(
                    messageFor(t, code, first?.code === 'cycle' ? 'cycle' : (first?.message ?? '')),
                  );
                  return;
                }

                setOnboarding(false);
                await reload();
              })();
            }}
          />
        </>
      )}
    </ScreenState>
  );
}
